import { validateReturnTo } from './authReturnTo.js';

const TUNNEL_ORIGIN_KEY = 'eachjewelry.tunnelOrigin';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const JEWELRY_LOT_PATH_PATTERN = /^\/jewelry\/(?:\d+|[a-z0-9-]+-\d+)$/;
const TITLE_MAX_LENGTH = 40;

export function isJewelryLotPath(path) {
    return typeof path === 'string' && JEWELRY_LOT_PATH_PATTERN.test(path.trim());
}

function normalizeOptionalString(value) {
    if (value == null) {
        return null;
    }

    if (typeof value !== 'string' && typeof value !== 'number') {
        return null;
    }

    const trimmed = String(value).trim();
    return trimmed || null;
}

function parseStoredOrigin(raw) {
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }

        const savedAt = Number(parsed.savedAt);
        if (Number.isFinite(savedAt) && Date.now() - savedAt > MAX_AGE_MS) {
            return null;
        }

        const path = validateReturnTo(parsed.path);
        if (!path || !isJewelryLotPath(path)) {
            return null;
        }

        return {
            path,
            title: normalizeOptionalString(parsed.title),
            lotNumber: normalizeOptionalString(parsed.lotNumber),
            savedAt: Number.isFinite(savedAt) ? savedAt : null,
        };
    } catch {
        return null;
    }
}

function writeStoredTunnelOrigin(origin) {
    const serialized = JSON.stringify(origin);

    for (const storage of [sessionStorage, localStorage]) {
        try {
            storage.setItem(TUNNEL_ORIGIN_KEY, serialized);
        } catch {
            // ignore quota / private mode
        }
    }
}

function readStoredTunnelOrigin() {
    for (const storage of [sessionStorage, localStorage]) {
        try {
            const origin = parseStoredOrigin(storage.getItem(TUNNEL_ORIGIN_KEY));
            if (origin) {
                return origin;
            }
        } catch {
            // ignore storage errors
        }
    }

    return null;
}

function clearStoredTunnelOrigin() {
    for (const storage of [sessionStorage, localStorage]) {
        try {
            storage.removeItem(TUNNEL_ORIGIN_KEY);
        } catch {
            // ignore
        }
    }
}

export function saveTunnelOrigin({ path, title, lotNumber }) {
    const validatedPath = validateReturnTo(path);
    if (!validatedPath || !isJewelryLotPath(validatedPath)) {
        return null;
    }

    const origin = {
        path: validatedPath,
        title: normalizeOptionalString(title),
        lotNumber: normalizeOptionalString(lotNumber),
        savedAt: Date.now(),
    };

    writeStoredTunnelOrigin(origin);

    return origin;
}

export function readTunnelOrigin(fallbackOrigin) {
    const fromStorage = readStoredTunnelOrigin();
    if (fromStorage) {
        return fromStorage;
    }

    if (!fallbackOrigin || typeof fallbackOrigin !== 'object') {
        return null;
    }

    const path = validateReturnTo(fallbackOrigin.path);
    if (!path || !isJewelryLotPath(path)) {
        return null;
    }

    return {
        path,
        title: normalizeOptionalString(fallbackOrigin.title),
        lotNumber: normalizeOptionalString(fallbackOrigin.lotNumber),
        savedAt: Number.isFinite(Number(fallbackOrigin.savedAt)) ? Number(fallbackOrigin.savedAt) : null,
    };
}

export function clearTunnelOrigin() {
    clearStoredTunnelOrigin();
}

function truncateTitle(title) {
    if (!title || title.length <= TITLE_MAX_LENGTH) {
        return title;
    }

    return `${title.slice(0, TITLE_MAX_LENGTH - 1).trim()}…`;
}

export function buildReturnToLotButtonLabel(origin) {
    if (!origin) {
        return 'Return to Your Lot →';
    }

    if (origin.lotNumber) {
        return `Back to Lot #${origin.lotNumber} →`;
    }

    if (origin.title) {
        return `Back to ${truncateTitle(origin.title)} →`;
    }

    return 'Return to Your Lot →';
}
